import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Send,
  Calculator,
  Building2,
  FileCheck,
  Camera,
  X,
  Upload,
  Image as ImageIcon,
  Loader2,
  Wrench,
  HardHat,
  Package,
  Boxes
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const ITEM_TYPES = [
  { value: 'equipment', label: 'Asset', icon: Wrench },
  { value: 'labour', label: 'Labour', icon: HardHat },
  { value: 'attachment', label: 'Attachment', icon: Package },
  { value: 'material', label: 'Material', icon: Boxes },
  { value: 'custom', label: 'Custom', icon: FileCheck }
];

const UNITS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'each', label: 'Each' },
  { value: 'tonne', label: 'Tonne' },
  { value: 'm3', label: 'm³' },
  { value: 'km', label: 'km' },
  { value: 'load', label: 'Load' }
];

const QuoteEdit = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id && id !== 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState('');
  
  // Reference data for dropdowns
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [materials, setMaterials] = useState([]);

  const [formData, setFormData] = useState({
    customer_id: '',
    site_id: '',
    pricing_type: 'hourly',
    valid_until: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    description: '',
    line_items: [],
    terms_and_conditions: '',
    notes: '',
    photos: []
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax_rate: 10,
    tax_amount: 0,
    total: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          customersRes, 
          equipmentTypesRes, 
          workerTypesRes, 
          attachmentTypesRes,
          materialsRes,
          termsRes
        ] = await Promise.all([
          api.get('/customers'),
          api.get('/equipment-types'),
          api.get('/worker-types'),
          api.get('/attachment-types').catch(() => ({ data: [] })),
          api.get('/tips').catch(() => ({ data: [] })),
          api.get('/quotes/settings/terms')
        ]);
        
        setCustomers(customersRes.data.filter(c => c.is_active !== false));
        // Equipment types endpoint returns array of strings
        setEquipmentTypes(equipmentTypesRes.data || []);
        setWorkerTypes(workerTypesRes.data || []);
        setAttachmentTypes(attachmentTypesRes.data || []);
        setMaterials(materialsRes.data || []);
        setDefaultTerms(termsRes.data.default_quote_terms || '');

        if (isEditing) {
          const quoteRes = await api.get(`/quotes/${id}`);
          const quote = quoteRes.data;
          setFormData({
            customer_id: quote.customer_id,
            site_id: quote.site_id || '',
            pricing_type: quote.pricing_type,
            valid_until: quote.valid_until,
            description: quote.description || '',
            line_items: quote.line_items || [],
            terms_and_conditions: quote.terms_and_conditions || '',
            notes: quote.notes || '',
            photos: quote.photos || []
          });
          setTotals({
            subtotal: quote.subtotal,
            tax_rate: quote.tax_rate,
            tax_amount: quote.tax_amount,
            total: quote.total
          });
          
          // Fetch sites for the customer
          if (quote.customer_id) {
            const sitesRes = await api.get(`/sites?customer_id=${quote.customer_id}`);
            setSites(sitesRes.data);
          }
        } else {
          // Set default terms for new quote
          setFormData(prev => ({
            ...prev,
            terms_and_conditions: termsRes.data.default_quote_terms || ''
          }));
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, id, isEditing]);

  useEffect(() => {
    // Recalculate totals when line items change
    const subtotal = formData.line_items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.rate || 0));
    }, 0);
    const tax_amount = Math.round(subtotal * (totals.tax_rate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax_amount) * 100) / 100;

    setTotals(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      tax_amount,
      total
    }));
  }, [formData.line_items, totals.tax_rate]);

  const handleCustomerChange = async (customerId) => {
    setFormData(prev => ({ ...prev, customer_id: customerId, site_id: '' }));
    
    if (customerId) {
      try {
        const sitesRes = await api.get(`/sites?customer_id=${customerId}`);
        setSites(sitesRes.data);
      } catch (error) {
        setSites([]);
      }
    } else {
      setSites([]);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          id: uuidv4(),
          item_type: 'equipment',
          description: '',
          equipment_id: '',
          equipment_type: '',
          worker_type_id: '',
          attachment_type_id: '',
          material_id: '',
          quantity: 1,
          unit: 'hour',
          rate: 0,
          total: 0,
          notes: ''
        }
      ]
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Recalculate item total
      if (field === 'quantity' || field === 'rate') {
        newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
      }
      
      // Auto-fill description when selecting from dropdown
      if (field === 'equipment_type' && value) {
        // Equipment types are strings, use them directly as description
        newItems[index].description = value;
      }
      if (field === 'worker_type_id' && value) {
        const selected = workerTypes.find(w => w.id === value);
        if (selected) {
          newItems[index].description = selected.name || '';
        }
      }
      if (field === 'attachment_type_id' && value) {
        const selected = attachmentTypes.find(a => a.id === value);
        if (selected) {
          newItems[index].description = selected.name || '';
        }
      }
      if (field === 'material_id' && value) {
        const selected = materials.find(m => m.id === value);
        if (selected) {
          newItems[index].description = selected.name || '';
        }
      }
      
      // Clear type-specific fields when changing item type
      if (field === 'item_type') {
        newItems[index].equipment_id = '';
        newItems[index].equipment_type = '';
        newItems[index].worker_type_id = '';
        newItems[index].attachment_type_id = '';
        newItems[index].material_id = '';
        newItems[index].description = '';
      }
      
      return { ...prev, line_items: newItems };
    });
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await api.post('/quotes/upload-photo', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.url) {
          newPhotos.push(response.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newPhotos.length > 0) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
      }));
      toast.success(`Uploaded ${newPhotos.length} photo(s)`);
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (sendAfterSave = false) => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    setSaving(true);
    try {
      let savedQuote;
      
      if (isEditing) {
        const response = await api.put(`/quotes/${id}`, formData);
        savedQuote = response.data;
        toast.success('Quote updated');
      } else {
        const response = await api.post('/quotes', formData);
        savedQuote = response.data;
        toast.success('Quote created');
      }

      if (sendAfterSave && savedQuote) {
        await api.post(`/quotes/${savedQuote.id}/send`);
        toast.success('Quote sent');
      }

      navigate('/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Get the appropriate dropdown options based on item type
  const getTypeSpecificDropdown = (item, index) => {
    switch (item.item_type) {
      case 'equipment':
        return (
          <div className="space-y-2">
            <Label className="text-xs">Asset Type</Label>
            <Select 
              value={item.equipment_type || ''} 
              onValueChange={(v) => updateLineItem(index, 'equipment_type', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select equipment type" />
              </SelectTrigger>
              <SelectContent>
                {equipmentTypes.map(eqType => (
                  <SelectItem key={eqType} value={eqType}>
                    {eqType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'labour':
        return (
          <div className="space-y-2">
            <Label className="text-xs">Worker Type</Label>
            <Select 
              value={item.worker_type_id || ''} 
              onValueChange={(v) => updateLineItem(index, 'worker_type_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select worker type" />
              </SelectTrigger>
              <SelectContent>
                {workerTypes.map(wt => (
                  <SelectItem key={wt.id} value={wt.id}>
                    {wt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      case 'attachment':
        return (
          <div className="space-y-2">
            <Label className="text-xs">Attachment Type</Label>
            <Select 
              value={item.attachment_type_id || ''} 
              onValueChange={(v) => updateLineItem(index, 'attachment_type_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select attachment" />
              </SelectTrigger>
              <SelectContent>
                {attachmentTypes.length > 0 ? (
                  attachmentTypes.map(at => (
                    <SelectItem key={at.id} value={at.id}>
                      {at.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="_none" disabled>No attachments available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        );
      case 'material':
        return (
          <div className="space-y-2">
            <Label className="text-xs">Material</Label>
            <Select 
              value={item.material_id || ''} 
              onValueChange={(v) => updateLineItem(index, 'material_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select material" />
              </SelectTrigger>
              <SelectContent>
                {materials.length > 0 ? (
                  materials.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="_none" disabled>No materials available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quote-edit-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/quotes')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">
            {isEditing ? 'Edit Quote' : 'Create Quote'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Update quote details' : 'Create a new quote for your customer'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Site */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Customer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Customer *</Label>
                  <Select 
                    value={formData.customer_id} 
                    onValueChange={handleCustomerChange}
                  >
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Site (Optional)</Label>
                  <Select 
                    value={formData.site_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}
                    disabled={!formData.customer_id || sites.length === 0}
                  >
                    <SelectTrigger data-testid="site-select">
                      <SelectValue placeholder={sites.length === 0 ? "No sites available" : "Select site"} />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>
                          {site.name || site.address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quote Settings */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Quote Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Pricing Type</Label>
                  <Select 
                    value={formData.pricing_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, pricing_type: v }))}
                  >
                    <SelectTrigger data-testid="pricing-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly Rate</SelectItem>
                      <SelectItem value="fixed">Fixed Price</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                    className="font-mono"
                    data-testid="valid-until-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card className="card-industrial">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Line Items
              </CardTitle>
              <Button onClick={addLineItem} variant="outline" size="sm" data-testid="add-line-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </CardHeader>
            <CardContent>
              {formData.line_items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No line items added yet</p>
                  <Button onClick={addLineItem} variant="outline" className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Item
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.line_items.map((item, index) => {
                    const ItemIcon = ITEM_TYPES.find(t => t.value === item.item_type)?.icon || FileCheck;
                    return (
                      <div 
                        key={item.id} 
                        className="p-4 border rounded-lg space-y-4 bg-muted/30"
                        data-testid={`line-item-${index}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs">Type</Label>
                              <Select 
                                value={item.item_type} 
                                onValueChange={(v) => updateLineItem(index, 'item_type', v)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ITEM_TYPES.map(type => {
                                    const Icon = type.icon;
                                    return (
                                      <SelectItem key={type.value} value={type.value}>
                                        <div className="flex items-center gap-2">
                                          <Icon className="h-4 w-4" />
                                          {type.label}
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Type-specific dropdown */}
                            {item.item_type !== 'custom' && getTypeSpecificDropdown(item, index)}
                            
                            <div className={`space-y-2 ${item.item_type === 'custom' ? 'md:col-span-2' : ''}`}>
                              <Label className="text-xs">Description</Label>
                              <Input
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                placeholder="Item description"
                              />
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => removeLineItem(index)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="font-mono"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Unit</Label>
                            <Select 
                              value={item.unit} 
                              onValueChange={(v) => updateLineItem(index, 'unit', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {UNITS.map(unit => (
                                  <SelectItem key={unit.value} value={unit.value}>
                                    {unit.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Rate ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                              className="font-mono"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Total</Label>
                            <div className="h-10 flex items-center px-3 bg-muted rounded-md font-mono font-semibold text-[#ffd108]">
                              {formatCurrency(item.quantity * item.rate)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description, Notes & Terms */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Description, Notes & Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Work Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the work being quoted (shown to customer)"
                  rows={3}
                  data-testid="description-input"
                />
                <p className="text-xs text-muted-foreground">
                  This description will be visible to the customer on the quote
                </p>
              </div>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Internal Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any internal notes (not shown to customer)"
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Terms & Conditions</Label>
                <Textarea
                  value={formData.terms_and_conditions}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                  placeholder="Enter terms and conditions"
                  rows={4}
                  data-testid="terms-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card className="card-industrial">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Reference Photos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload photos for future reference (site conditions, equipment needs, etc.)
              </p>
              
              {/* Photo Grid */}
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border">
                      <img 
                        src={photo} 
                        alt={`Quote photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-photo-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photos
                    </>
                  )}
                </Button>
                {formData.photos.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {formData.photos.length} photo{formData.photos.length !== 1 ? 's' : ''} attached
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Totals & Actions */}
        <div className="space-y-6">
          <Card className="card-industrial sticky top-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">GST ({totals.tax_rate}%)</span>
                  <span className="font-mono">{formatCurrency(totals.tax_amount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-mono font-bold text-xl text-[#ffd108]">
                    {formatCurrency(totals.total)}
                  </span>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Button 
                  onClick={() => handleSave(false)} 
                  disabled={saving}
                  className="w-full"
                  variant="outline"
                  data-testid="save-draft-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save as Draft'}
                </Button>
                <Button 
                  onClick={() => handleSave(true)} 
                  disabled={saving}
                  className="w-full btn-volt"
                  data-testid="save-send-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save & Send'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Quotes are valid for 30 days by default
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuoteEdit;
